import React, { useState } from 'react';
import CirclePanelHeader from './components/CirclePanelHeader';
import CircleTabNav from './components/CircleTabNav';
import CooHero from './components/CooHero';
import DomainLegend from './components/DomainLegend';
import DomainSection from './components/DomainSection';
import PageFooter from './components/PageFooter';
import ZoneKpiSection from './components/ZoneKpiSection';
import cooDashboardData from './data/cooDashboardData';

/**
 * The body of the COO Dashboard — hero, legend, zone KPIs, circle tabs,
 * per-circle panels, footer.
 *
 * Extracted from CooDashboardPage so it can render in two places:
 *   1. Standalone at /coo-dashboard (via CooDashboardPage — wraps this with
 *      CooTopBar + BubbleShell).
 *   2. Inline as the "Executive" domain in App.js (where the app already
 *      provides its own header and the Drishti bubble at page level).
 *
 * No outer max-width / padding classes — the host decides layout. Uses
 * <div> instead of <main> to avoid nested-main HTML when embedded.
 */
export default function CooDashboardContent({
  wrapperClassName = '',
  showHero = true,
  excludeDomainTags = [],
}) {
  const [activeCircle, setActiveCircle] = useState(cooDashboardData.circles[0]?.id || 'HP');

  const excludePrefixes = excludeDomainTags.map((t) => t.toUpperCase());
  const shouldKeep = (item) =>
    !excludePrefixes.some((p) => (item.domainTag || '').toUpperCase().startsWith(p));
  const shouldKeepLegend = (item) =>
    !excludePrefixes.some((p) => (item.label || '').toUpperCase().startsWith(p));

  const legend = excludePrefixes.length
    ? { ...cooDashboardData.legend, items: cooDashboardData.legend.items.filter(shouldKeepLegend) }
    : cooDashboardData.legend;

  return (
    <div data-coo="content" className={`space-y-6 ${wrapperClassName}`}>
      {showHero ? <CooHero meta={cooDashboardData.meta} /> : null}
      <DomainLegend legend={legend} alert={cooDashboardData.alert} />

      {cooDashboardData.zoneSummarySections.map((section) => {
        const items = excludePrefixes.length
          ? section.items.filter(shouldKeep)
          : section.items;
        return (
          <ZoneKpiSection key={section.title} section={{ ...section, items }} />
        );
      })}

      <CircleTabNav circles={cooDashboardData.circles} activeCircle={activeCircle} onChange={setActiveCircle} />

      <div className="space-y-5">
        {cooDashboardData.circles.map((circle) => {
          const isActive = circle.id === activeCircle;
          return (
            <section
              key={circle.id}
              id={`circle-panel-${circle.id}`}
              data-coo="circle-panel"
              role="tabpanel"
              aria-labelledby={`circle-tab-${circle.id}`}
              hidden={!isActive}
              className={isActive ? 'block' : 'hidden'}
            >
              <CirclePanelHeader circle={circle} />
              <div className="mt-4 space-y-4">
                {circle.sections.map((section) => (
                  <DomainSection key={section.id} section={section} />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      <PageFooter lines={cooDashboardData.footer} />
    </div>
  );
}
